import React, { useState } from 'react';
import classNameBind from 'classnames/bind';
import _differenceBy from 'lodash/differenceBy';

import { SearchableMultiselectListbox } from '../SearchableMultiselectListbox';
import { Arrows } from '../Arrows';
import styles from './OrderSelection.module.css';
import { mapDisabled } from './mapDisabled';

const bindClasses = classNameBind.bind(styles);
const getItemById = items => id => items.find(item => item.id === id);

const OrderSelection = ({
  name,
  items,
  value,
  pagination,
  onChange,
  disabledTooltipText,
  search,
  t,
  leftLoading,
  rightLoading,
  leftError,
  rightError,
  leftHeader,
  rightHeader,
  dataId,
}) => {
  const [checkedLeft, setCheckedLeft] = useState([]);
  const [checkedRight, setCheckedRight] = useState([]);

  const createEvent = newValue => ({
    target: {
      name,
      value: newValue,
    },
  });

  const onReorder = ({ source, destination }) => {
    const reorder = (list, startIndex, endIndex) => {
      const result = Array.from(list);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);

      return result;
    };

    if (!destination) {
      return;
    }

    const sourceIndex = source.index;
    const destinationIndex = destination.index;

    const newList = reorder(value, sourceIndex, destinationIndex);

    onChange(createEvent(newList));
  };

  const moveToPrioritized = () => {
    onChange(createEvent([
      ...value,
      ...checkedRight,
    ]));
    setCheckedRight([]);
  };

  const removeFromPrioritized = () => {
    onChange(createEvent(_differenceBy(value, checkedLeft, 'id')));
    setCheckedLeft([]);
  };

  const handleItemSelected = (event) => {
    const currentlyCheckedItemIds = event.target.value.filter(id => !getItemById(checkedRight)(id));
    const currentlyCheckedItems = currentlyCheckedItemIds.map(getItemById(items));

    const newValue = [
      ...checkedRight,
      ...currentlyCheckedItems,
    ].filter((item) => {
      const isNotInEvent = !(event.target.value.includes(item.id));
      const isOnCurrentPage = !!getItemById(items)(item.id);

      return !(isNotInEvent && isOnCurrentPage);
    });

    setCheckedRight(newValue);
  };

  return (
    <div className={bindClasses('wrapper')} data-ppuc-id={dataId}>
      <div className={bindClasses('column')}>
        <SearchableMultiselectListbox
          id="priority-left"
          header={leftHeader}
          dataId={`${dataId}.left`}
          onReorder={onReorder}
          name="left"
          options={value}
          pagination={{
            limit: Infinity,
            offset: 0,
            limitOptions: [Infinity],
            total: 0,
          }}
          onChange={(event) => {
            setCheckedLeft(
              event.target.value.map(getItemById(value)),
            );
          }}
          value={checkedLeft.map(item => item.id)}
          isLoading={leftLoading}
          failedMessage={leftError}
        />
      </div>
      <Arrows
        dataId={`${dataId}.Arrows`}
        leftArrowOnClick={moveToPrioritized}
        rightArrowOnClick={removeFromPrioritized}
        leftArrowDisabled={checkedRight.length === 0}
        rightArrowDisabled={checkedLeft.length === 0}
      />
      <div className={bindClasses('column')}>
        <SearchableMultiselectListbox
          id="priority-right"
          header={rightHeader}
          dataId={`${dataId}.right`}
          options={mapDisabled(items, value)}
          search={{
            ...search,
            placeholder: t('OrderSelection.search-placeholder'),
          }}
          name="right"
          pagination={pagination}
          onChange={handleItemSelected}
          value={checkedRight.map(item => item.id)}
          disabledTooltipText={disabledTooltipText}
          isLoading={rightLoading}
          failedMessage={rightError}
        />
      </div>
    </div>
  );
};

OrderSelection.defaultProps = {
  items: [],
  value: [],
  onChange: () => {},
  disabledTooltipText: null,
  search: {
    value: '',
    onChange: () => {},
  },
  leftLoading: false,
  rightLoading: false,
  leftError: null,
  rightError: null,
  leftHeader: null,
  rightHeader: null,
  dataId: 'OrderSelection',
};

export { OrderSelection };
